import * as React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Popper,
  Chip,
  Typography,
  InputAdornment,
  ClickAwayListener,
  Collapse,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Cancel, Close } from "@mui/icons-material";
import {
  StyledCheckbox,
  StyledMenuItemContainer,
  StyledMenuListItemText,
  StyledSubMenuItemContainer,
  HtmlTooltip,
} from "../StyledComponents/styled";
import { CustomTextInput } from "../TextInputs/style";
import { erase_duplicates } from "../../../api_calls/utils";

const MAX_LABEL_CHARS = 50;
const CHAR_WIDTH_PX = 6.5;
const DROPDOWN_PADDING_PX = 80;
const BOTTOM_MARGIN_PX = 100;
const TOP_MARGIN_PX = 8;
const MIN_DROPDOWN_HEIGHT = 150;
const FLIP_THRESHOLD_TO_TOP = 200;
const FLIP_THRESHOLD_TO_BOTTOM = 250;

const DROPDOWN_OPENED_EVENT = "dropdown-singleton-opened";

export default function GroupedMultiSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  usedFor,
  onBlurCall,
  showSelectedValues,
  readOnlyFlag,
  groupParam,
  disabledIfSelectedOption,
  showGroupNames,
  disableSearch,
  canNotClear,
}) {
  const anchorRef = useRef(null);
  const itemRefs = useRef({});
  const paperRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef({});
  const popperRef = useRef(null);

  const [selectedValues, setSelectedValues] = useState(defaultVal);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(MIN_DROPDOWN_HEIGHT);
  const [expanded, setExpanded] = useState({});
  const [anchorIsFixed, setAnchorIsFixed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Track placement both as state (for render) and as ref (for stable reads in callbacks)
  const placementRef = useRef("bottom-start");
  const [actualPlacement, setActualPlacement] = useState("bottom-start");

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const shrink = isFocused || open || (selectedValues?.length ?? 0) > 0;

  const instanceId = useRef(
    elemId || `grouped-multi-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const updatePlacement = useCallback((p) => {
    placementRef.current = p;
    setActualPlacement(p);
  }, []);

  // Determine placement using hysteresis
  const computePlacement = useCallback(() => {
    if (!anchorRef.current) return placementRef.current;

    const { bottom } = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    const current = placementRef.current;
    const isTop = current.startsWith("top");

    if (!isTop && spaceBelow < FLIP_THRESHOLD_TO_TOP) return "top-start";
    if (isTop && spaceBelow > FLIP_THRESHOLD_TO_BOTTOM) return "bottom-start";
    return current;
  }, []);

  // Compute max dropdown height based on resolved placement
  const computeHeight = useCallback((placement) => {
    if (!anchorRef.current) return MIN_DROPDOWN_HEIGHT;

    const { top, bottom } = anchorRef.current.getBoundingClientRect();
    const openingUpward = placement.startsWith("top");
    const available = openingUpward
      ? top - TOP_MARGIN_PX
      : window.innerHeight - bottom - BOTTOM_MARGIN_PX;

    return Math.max(available, MIN_DROPDOWN_HEIGHT);
  }, []);

  // Resolve placement first, then derive height from that placement
  const recalculate = useCallback(() => {
    const placement = computePlacement();
    updatePlacement(placement);
    setDropdownMaxHeight(computeHeight(placement));
  }, [computePlacement, computeHeight, updatePlacement]);

  const detectAnchorIsFixed = useCallback(() => {
    let el = anchorRef.current?.parentElement;

    // Pre-scan the full ancestor chain for a Dialog before evaluating overflow;
    // DialogContent (overflow: auto) sits below the Dialog root in the DOM, so a
    // simple top-down traversal would return early at the overflow node and never
    // reach role="dialog"
    let insideDialog = false;
    let scanEl = el;
    while (scanEl && scanEl !== document.body) {
      if (scanEl.getAttribute("role") === "dialog") { insideDialog = true; break; }
      scanEl = scanEl.parentElement;
    }

    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (/auto|scroll|overlay/.test(overflow)) {
        // Anchor scrolls inside this container; fixed strategy is required when
        // that container belongs to a Dialog so the Popper can track the anchor
        return insideDialog;
      }

      if (style.position === "sticky" || style.position === "fixed") return true;

      el = el.parentElement;
    }

    return insideDialog;
  }, []);

  const close = useCallback(
    (shouldRefocus = false) => {
      setOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
      if (shouldRefocus) anchorRef.current?.focus();
      if (onBlurCall) onBlurCall();
    },
    [onBlurCall]
  );

  useEffect(() => {
    const handleOtherDropdownOpened = (e) => {
      if (e.detail?.id !== instanceId.current && open) close();
    };

    window.addEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    return () => {
      window.removeEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    };
  }, [open, close]);

  useEffect(() => {
    if (open) {
      const event = new CustomEvent(DROPDOWN_OPENED_EVENT, {
        detail: { id: instanceId.current },
      });
      window.dispatchEvent(event);
    }
  }, [open]);

  // On open: resolve placement first, then compute height; focus search input or
  // the Paper itself so keyboard navigation works even without a search field
  useEffect(() => {
    if (!open) return;

    setAnchorIsFixed(detectAnchorIsFixed());
    recalculate();

    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setTimeout(() => paperRef.current?.focus(), 0);
    }
  }, [open, detectAnchorIsFixed, recalculate, showSearch]);

  // On viewport resize or zoom: recalculate placement and height while open
  useEffect(() => {
    if (!open) return;

    const handleResize = () => recalculate();
    const vp = window.visualViewport;

    if (vp) {
      vp.addEventListener("resize", handleResize);
      return () => vp.removeEventListener("resize", handleResize);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, recalculate]);

  // Close when anchor exits the viewport in the direction the dropdown is open;
  // for fixed-strategy anchors also force a synchronous position update on scroll
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (anchorRef.current) {
        const { top, bottom } = anchorRef.current.getBoundingClientRect();
        const openingUpward = placementRef.current.startsWith("top");
        if (openingUpward && top > window.innerHeight) { close(false); return; }
        if (!openingUpward && bottom < 0) { close(false); return; }
      }
      // Absolute-strategy poppers follow document scroll natively; only fixed ones need JS nudging
      if (anchorIsFixed) popperRef.current?.forceUpdate();
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [open, anchorIsFixed, close]);

  useEffect(() => {
    setSelectedValues(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const isSelected = useCallback(
    (option) => selectedValues.some((val) => selectionCheck(option, val)),
    [selectedValues, selectionCheck]
  );

  const isOptionDisabled = useCallback(
    (option) => {
      if (!disabledIfSelectedOption || disabledIfSelectedOption.length === 0) return false;

      const hasOtherSelected = selectedValues?.some(
        (selectedOption) =>
          !disabledIfSelectedOption?.some(
            (disabledOption) => disabledOption === selectedOption.label
          )
      );

      const optionIsDisabled =
        hasOtherSelected && disabledIfSelectedOption.includes(option.label)
          ? true
          : disabledIfSelectedOption?.some((disabledOption) =>
              selectedValues.some(
                (selectedOption) =>
                  selectedOption.label === disabledOption &&
                  option.label !== disabledOption
              )
            );

      return optionIsDisabled;
    },
    [disabledIfSelectedOption, selectedValues]
  );

  const handleSelect = useCallback((option) => {
    if (isDisabled) return;
    if (isOptionDisabled(option)) return;

    const alreadySelected = isSelected(option);
    const newSelection = alreadySelected
      ? selectedValues.filter((val) => !selectionCheck(option, val))
      : [...selectedValues, option];

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, isOptionDisabled, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const handleRemove = (optionToRemove) => {
    if (isDisabled) return;

    const newSelection = selectedValues.filter(
      (val) => !selectionCheck(optionToRemove, val)
    );
    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValues([]);
    onChangeCall([]);
  };

  const handleCategoryToggle = (groupName) => {
    const wasExpanded = expanded[groupName];
    setExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
    setHighlightedIndex(-1);

    if (!wasExpanded) {
      setTimeout(() => {
        const item = itemRefs.current[groupName];
        const container = paperRef.current;

        if (!item || !container) return;

        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (itemRect.bottom >= containerRect.bottom) {
          const scrollOffset = itemRect.top - containerRect.top - 8;
          container.scrollTo({
            top: container.scrollTop + scrollOffset,
            behavior: "smooth",
          });
        }
      }, 300);
    }
  };

  const handleCategorySelect = (groupOptions, allSelected, e) => {
    e.stopPropagation();
    if (isDisabled) return;

    const selectableGroupOptions = groupOptions.filter((opt) => !isOptionDisabled(opt));
    if (selectableGroupOptions.length === 0) return;

    let newSelection;
    if (allSelected) {
      newSelection = selectedValues.filter(
        (val) => !selectableGroupOptions.some((opt) => selectionCheck(opt, val))
      );
    } else {
      const optionsToAdd = selectableGroupOptions.filter((opt) => !isSelected(opt));
      newSelection = [...selectedValues, ...optionsToAdd];
    }

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  };

  const uniqueOptions = useMemo(() => erase_duplicates(options), [options]);

  const groupedCategories = useMemo(() => {
    const groups = {};

    uniqueOptions.forEach((option) => {
      const groupName = option[groupParam] || "Other";
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(option);
    });

    const sortedGroupNames = Object.keys(groups).sort((a, b) => {
      const aNum = parseInt(a.split("-")[0], 10);
      const bNum = parseInt(b.split("-")[0], 10);

      const aHasNum = !Number.isNaN(aNum);
      const bHasNum = !Number.isNaN(bNum);

      if (aHasNum && bHasNum) return aNum - bNum;
      return a.localeCompare(b, undefined, { numeric: true });
    });

    return sortedGroupNames.map((groupName) => ({
      groupName,
      options: groups[groupName].sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { numeric: true })
      ),
    }));
  }, [uniqueOptions, groupParam]);

  const searchValue = searchTerm.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!showSearch || !searchValue) return groupedCategories;

    return groupedCategories
      .map((cat) => {
        const lowerCaseGroupName = cat.groupName.toLowerCase();

        if (lowerCaseGroupName.includes(searchValue)) return cat;

        const filteredOptions = cat.options.filter((opt) => {
          const optLabel = String(opt?.label ?? "").toLowerCase();
          return optLabel.includes(searchValue);
        });

        if (filteredOptions.length > 0) return { ...cat, options: filteredOptions };

        return null;
      })
      .filter((cat) => cat !== null);
  }, [groupedCategories, searchValue, showSearch]);

  const isSearchActive = showSearch && searchTerm.trim().length > 0;

  // Flat list of all options currently visible after filtering, used for
  // the "Select All Results" bulk action and its checkbox state
  const filteredFlatOptions = useMemo(
    () => filteredCategories.flatMap((cat) => cat.options),
    [filteredCategories]
  );

  const selectableFilteredOptions = useMemo(
    () => filteredFlatOptions.filter((opt) => !isOptionDisabled(opt)),
    [filteredFlatOptions, isOptionDisabled]
  );

  const allFilteredSelected = useMemo(() => {
    if (selectableFilteredOptions.length === 0) return false;
    return selectableFilteredOptions.every((opt) => isSelected(opt));
  }, [selectableFilteredOptions, isSelected]);

  const someFilteredSelected = useMemo(() => {
    const count = selectableFilteredOptions.filter((opt) => isSelected(opt)).length;
    return count > 0 && !allFilteredSelected;
  }, [selectableFilteredOptions, isSelected, allFilteredSelected]);

  const handleSelectAllFiltered = useCallback(() => {
    if (isDisabled) return;

    const allSel =
      selectableFilteredOptions.length > 0 &&
      selectableFilteredOptions.every((opt) => isSelected(opt));

    let newSelection;
    if (allSel) {
      newSelection = selectedValues.filter(
        (val) => !selectableFilteredOptions.some((opt) => selectionCheck(opt, val))
      );
    } else {
      const toAdd = selectableFilteredOptions.filter((opt) => !isSelected(opt));
      newSelection = [...selectedValues, ...toAdd];
    }

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, selectableFilteredOptions, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const flatOptionsList = useMemo(() => {
    const list = [];

    // Index 0 is reserved for the "Select All Results" row when search is active
    if (isSearchActive) {
      list.push({ type: "select-all-results" });
    }

    if (showGroupNames) {
      filteredCategories.forEach((cat) => {
        list.push({ type: "group", groupName: cat.groupName, data: cat });
        if (expanded[cat.groupName]) {
          cat.options.forEach((opt) => {
            list.push({ type: "option", option: opt, groupName: cat.groupName });
          });
        }
      });
    } else {
      filteredCategories.flatMap((cat) =>
        cat.options.map((opt) => ({ type: "option", option: opt }))
      ).forEach((item) => list.push(item));
    }

    return list;
  }, [filteredCategories, expanded, showGroupNames, isSearchActive]);

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`item-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = flatOptionsList.length;
      if (totalCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatOptionsList.length) return;

          const item = flatOptionsList[highlightedIndex];
          if (item.type === "select-all-results") {
            handleSelectAllFiltered();
          } else if (item.type === "group") {
            handleCategoryToggle(item.groupName);
          } else if (item.type === "option" && !isOptionDisabled(item.option)) {
            handleSelect(item.option);
          }
          break;

        case " ":
          if (e.target.tagName === "INPUT") return;
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatOptionsList.length) return;

          const spaceItem = flatOptionsList[highlightedIndex];
          if (spaceItem.type === "select-all-results") {
            handleSelectAllFiltered();
          } else if (spaceItem.type === "option" && !isOptionDisabled(spaceItem.option)) {
            handleSelect(spaceItem.option);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [open, flatOptionsList, highlightedIndex, close, isOptionDisabled, scrollHighlightedIntoView, handleSelect, handleSelectAllFiltered]
  );

  const dropdownWidth = useMemo(() => {
    const anchorWidth = anchorRef.current?.offsetWidth ?? 0;

    let maxLabelLength = 0;
    uniqueOptions.forEach((opt) => {
      const labelLength = String(opt?.label ?? "").length;
      if (labelLength > maxLabelLength) maxLabelLength = labelLength;
    });

    const clampedLength = Math.min(maxLabelLength, MAX_LABEL_CHARS);
    const contentWidth = clampedLength * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX;

    return Math.max(anchorWidth, contentWidth);
  }, [uniqueOptions, open]);

  const maxWidthStyle = enableFullWidth
    ? { maxWidth: "100%" }
    : { maxWidth: "220px", width: "100%" };

  // flatIndex is mutated during render to map visual positions to keyboard navigation indices;
  // when search is active, index 0 is already consumed by the "Select All Results" row
  let flatIndex = isSearchActive ? 0 : -1;

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) close(false);
      }}
    >
      <Box sx={{ width: "100%", ...maxWidthStyle }}>
        <Box
          id={elemId}
          ref={anchorRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={isDisabled ? -1 : 0}
          onClick={() => {
            if (!isDisabled) setOpen((prev) => !prev);
          }}
          onFocus={() => {
            if (!isDisabled) setIsFocused(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (isDisabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
            if (e.key === "Escape") close();
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              if (!open) setOpen(true);
            }
          }}
          sx={{
            position: "relative",
            cursor: isDisabled ? "default" : "pointer",
            outline: "none",
            pt: 2.1,
            borderBottom: "1px solid #AF3241",
            opacity: isDisabled ? 0.6 : 1,
            "& .cap-clear-btn": { opacity: 0 },
            "&:hover .cap-clear-btn": { opacity: 1 },
            "& .cap-clear-btn-visible": { opacity: 1 },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -1,
              width: shrink ? "100%" : "0%",
              height: "2px",
              backgroundColor: "#AF3241",
              transition: "width 180ms ease",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: shrink ? 0 : 22,
              fontSize: shrink ? 12 : 14,
              color: "#AF3241",
              pointerEvents: "none",
              transformOrigin: "top left",
              transition: "all 160ms ease",
            }}
          >
            {label}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexWrap: "wrap",
                overflowX: "auto",
                overflowY: "hidden",
                gap: 0.5,
                py: 0.5,
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { height: 6 },
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) setOpen(true);
              }}
            >
              {selectedValues.length > 0 &&
                selectedValues.map((option, index) => (
                  <Chip
                    key={option?.label ?? index}
                    size="small"
                    label={option.label}
                    onClick={(e) => e.stopPropagation()}
                    onDelete={
                      isDisabled
                        ? undefined
                        : (e) => {
                            e.stopPropagation();
                            handleRemove(option);
                          }
                    }
                    deleteIcon={<Cancel sx={{ color: "#AF3241" }} />}
                    sx={{
                      flex: "0 0 auto",
                      backgroundColor: "#AF3241",
                      color: "#fff",
                      fontSize: "12px",
                      height: 24,
                      "& .MuiChip-deleteIcon": { color: "#fff" },
                    }}
                  />
                ))}
            </Box>

            <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 0.4 }}>
              {!canNotClear && selectedValues.length > 0 && !isDisabled && (
                <Close
                  className={`cap-clear-btn${open || isFocused ? " cap-clear-btn-visible" : ""}`}
                  onClick={handleClear}
                  sx={{ cursor: "pointer", fontSize: 18 }}
                />
              )}
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>
        </Box>

        {fieldError && fieldError !== "" && (
          <Typography
            variant="caption"
            sx={{ color: "#AF3241", mt: 0.5, display: "block" }}
          >
            {fieldError}
          </Typography>
        )}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          popperRef={popperRef}
          placement={actualPlacement}
          popperOptions={{ strategy: anchorIsFixed ? "fixed" : "absolute" }}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "eventListeners",
              options: { scroll: false, resize: false },
            },
            {
              // Placement and height are managed by recalculate(); the dropdown
              // scrolls naturally with its anchor rather than being pinned to the viewport
              name: "preventOverflow",
              enabled: false,
            },
            {
              // Flip is managed by recalculate(); disable Popper's built-in modifier
              name: "flip",
              enabled: false,
            },
          ]}
          style={{
            minWidth: anchorRef.current?.offsetWidth ?? undefined,
            width: dropdownWidth,
            maxWidth: MAX_LABEL_CHARS * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX,
          }}
        >
          <Paper
            ref={paperRef}
            role="listbox"
            tabIndex={-1}
            sx={{
              maxHeight: dropdownMaxHeight,
              overflowY: "auto",
              overflowX: "hidden",
              outline: "none",
            }}
            onMouseDown={(e) => {
              if (e.target.tagName !== "INPUT") e.preventDefault();
              e.stopPropagation();
            }}
            onKeyDown={handleListKeyDown}
          >
            {showSearch && (
              <Box sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
                <CustomTextInput
                  inputRef={searchInputRef}
                  id={elemId + "_search"}
                  size="small"
                  fullWidth
                  autoComplete="off"
                  spellCheck={false}
                  label="Search"
                  value={searchTerm}
                  variant="standard"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleListKeyDown}
                  InputProps={{
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <Close
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchTerm("");
                          }}
                          sx={{ cursor: "pointer", color: "#AF3241" }}
                        />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
            )}

            {/* Bulk selection row for all currently filtered options */}
            {isSearchActive && (
              <StyledMenuItemContainer
                ref={(el) => {
                  optionRefs.current["item-0"] = el;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAllFiltered();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHighlightedIndex(0)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor:
                    highlightedIndex === 0 ? "rgba(175, 50, 65, 0.08)" : "transparent",
                }}
              >
                <StyledCheckbox
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <StyledMenuListItemText primary="Select All Results" />
              </StyledMenuItemContainer>
            )}

            {showGroupNames === true ? (
              filteredCategories.map((cat) => {
                const { groupName, options: groupOptions } = cat;
                const isExpanded = !!expanded[groupName];

                const fullGroupOptions =
                  groupedCategories.find((g) => g.groupName === groupName)?.options || [];

                const selectableGroupOptions = fullGroupOptions.filter(
                  (opt) => !isOptionDisabled(opt)
                );
                const selectedCount = selectableGroupOptions.filter((opt) =>
                  isSelected(opt)
                ).length;
                const allSelected =
                  selectableGroupOptions.length > 0 &&
                  selectedCount === selectableGroupOptions.length;
                const someSelected = selectedCount > 0 && !allSelected;

                flatIndex++;
                const groupIndex = flatIndex;

                return (
                  <Box
                    key={groupName}
                    ref={(el) => {
                      itemRefs.current[groupName] = el;
                    }}
                    sx={{ width: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <StyledMenuItemContainer
                      ref={(el) => {
                        optionRefs.current[`item-${groupIndex}`] = el;
                      }}
                      onClick={() => handleCategoryToggle(groupName)}
                      onMouseEnter={() => setHighlightedIndex(groupIndex)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          highlightedIndex === groupIndex
                            ? "rgba(175, 50, 65, 0.08)"
                            : "transparent",
                      }}
                    >
                      <StyledCheckbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onClick={(e) =>
                          handleCategorySelect(fullGroupOptions, allSelected, e)
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <StyledMenuListItemText primary={groupName} />
                      {isExpanded ? (
                        <ExpandLessIcon fontSize="small" sx={{ ml: 1 }} />
                      ) : (
                        <ExpandMoreIcon fontSize="small" sx={{ ml: 1 }} />
                      )}
                    </StyledMenuItemContainer>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      {groupOptions.map((option, index) => {
                        const optionKey = option?.label ?? index;
                        const checked = isSelected(option);
                        const optionDisabled = isOptionDisabled(option);

                        flatIndex++;
                        const optIndex = flatIndex;

                        const optionContent = (
                          <StyledSubMenuItemContainer
                            ref={(el) => {
                              optionRefs.current[`item-${optIndex}`] = el;
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!optionDisabled) handleSelect(option);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseEnter={() => setHighlightedIndex(optIndex)}
                            sx={{
                              pl: 5,
                              display: "flex",
                              alignItems: "center",
                              opacity: optionDisabled ? 0.5 : 1,
                              cursor: optionDisabled ? "default" : "pointer",
                              backgroundColor:
                                highlightedIndex === optIndex
                                  ? "rgba(175, 50, 65, 0.08)"
                                  : "transparent",
                            }}
                          >
                            <StyledCheckbox
                              checked={checked}
                              disabled={optionDisabled}
                              onMouseDown={(e) => e.stopPropagation()}
                            />
                            <StyledMenuListItemText primary={option.label} />
                          </StyledSubMenuItemContainer>
                        );

                        if (optionDisabled) {
                          return (
                            <HtmlTooltip
                              key={optionKey}
                              title="This option is unavailable"
                              arrow
                            >
                              <Box>{optionContent}</Box>
                            </HtmlTooltip>
                          );
                        }

                        return (
                          <React.Fragment key={optionKey}>
                            {optionContent}
                          </React.Fragment>
                        );
                      })}
                    </Collapse>
                  </Box>
                );
              })
            ) : (
              filteredCategories.flatMap((cat) =>
                cat.options.map((option, index) => {
                  const optionKey = option?.label ?? index;
                  const checked = isSelected(option);
                  const optionDisabled = isOptionDisabled(option);

                  flatIndex++;
                  const optIndex = flatIndex;

                  const optionContent = (
                    <StyledMenuItemContainer
                      ref={(el) => {
                        optionRefs.current[`item-${optIndex}`] = el;
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!optionDisabled) handleSelect(option);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseEnter={() => setHighlightedIndex(optIndex)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        opacity: optionDisabled ? 0.5 : 1,
                        cursor: optionDisabled ? "default" : "pointer",
                        backgroundColor:
                          highlightedIndex === optIndex
                            ? "rgba(175, 50, 65, 0.08)"
                            : "transparent",
                      }}
                    >
                      <StyledCheckbox
                        checked={checked}
                        disabled={optionDisabled}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <Typography
                        variant="body2"
                        className="selectorOptions"
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          color: optionDisabled ? "#9e9e9e" : "inherit",
                        }}
                      >
                        {option.label}
                      </Typography>
                    </StyledMenuItemContainer>
                  );

                  if (optionDisabled) {
                    return (
                      <HtmlTooltip
                        key={optionKey}
                        title="This option is unavailable"
                        arrow
                      >
                        <Box>{optionContent}</Box>
                      </HtmlTooltip>
                    );
                  }

                  return (
                    <React.Fragment key={optionKey}>
                      {optionContent}
                    </React.Fragment>
                  );
                })
              )
            )}

            {filteredCategories.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  No options found
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
